import { notifications } from "@mantine/notifications"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import {
  CreateBusinessPartnerRequestDto,
  findContactrMappingQueryKey,
  UpdateBusinessPartnerRequestDto,
  useUpdateBusinessPartner,
} from "~/gen/api"
import { useCreateBusinessPartner } from "~/gen/api/hooks/useCreateBusinessPartner"
import { organizationRequestDtoCountryEnum } from "~/gen/api/types"

type ContactRequestDto =
  | CreateBusinessPartnerRequestDto
  | (UpdateBusinessPartnerRequestDto & { id: string })
type UseSupplierMappingFormProps = {
  initialValues?: ContactRequestDto
}

const useSupplierMappingForm = ({
  initialValues,
}: UseSupplierMappingFormProps) => {
  const { t } = useTranslation("suppliers")
  const [supplierMapping, setSupplierMapping] = useState<ContactRequestDto>(
    initialValues ?? {
      supplierId: "",
      name: "",
      email: "",
      preferedLangageIso6391: "EN",
      organization: {
        name: "",
        street: "",
        city: "",
        postalCode: "",
        country: organizationRequestDtoCountryEnum.FR,
        registrationId: [],
        businessRelationship: {
          isActive: true,
          supplierCode: "",
          type: null,
        },
      },
    }
  )

  const { mutate: createSupplierMapping, isPending: isCreating } =
    useCreateBusinessPartner({
      mutation: {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: findContactrMappingQueryKey({ searchSupplier: "" }),
          })
          notifications.show({
            title: t("repository.create.success.title"),
            message: t("repository.create.success.message"),
            color: "teal",
          })
        },
        onError: (error) => {
          notifications.show({
            title: t("repository.create.error.title"),
            message: t("repository.create.error.message", {
              error: error.response?.data,
            }),
            color: "red",
          })
        },
      },
    })

  const { mutate: updateSupplierMapping, isPending: isUpdating } =
    useUpdateBusinessPartner({
      mutation: {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: findContactrMappingQueryKey({ searchSupplier: "" }),
          })
          notifications.show({
            title: t("repository.update.success.title"),
            message: t("repository.update.success.message"),
            color: "teal",
          })
        },
        onError: (error) => {
          notifications.show({
            title: t("repository.update.error.title"),
            message: t("repository.update.error.message", {
              error: error.response?.data,
            }),
            color: "red",
          })
        },
      },
    })

  const handleSupplierMapping = (values?: ContactRequestDto) => {
    if ("supplierId" in supplierMapping && supplierMapping.supplierId !== "") {
      updateSupplierMapping({
        id: supplierMapping.id,
        data: {
          ...values,
          name: values?.name ?? supplierMapping.name,
          email: values?.email ?? supplierMapping.email,
          preferedLangageIso6391:
            values?.preferedLangageIso6391 ??
            supplierMapping.preferedLangageIso6391,
          organization: values?.organization ?? supplierMapping.organization,
          supplierId: supplierMapping.supplierId,
        },
      })
    } else {
      createSupplierMapping({
        data: {
          ...values,
          name: values?.name ?? supplierMapping.name,
          email: values?.email ?? supplierMapping.email,
          preferedLangageIso6391:
            values?.preferedLangageIso6391 ??
            supplierMapping.preferedLangageIso6391,
          organization: values?.organization ?? supplierMapping.organization,
        },
      })
    }
  }

  return {
    supplierMapping,
    setSupplierMapping,
    handleSupplierMapping,
    isCreating,
    isUpdating,
  }
}

export default useSupplierMappingForm
